import {ChangeTabFn, defaultBorderColor, TabControl, useNotifications} from "peggirkit";
import React, {useMemo, useRef} from "react";
import {twJoin} from "tailwind-merge";
import SummarySidebar from "@widgets/pos/order/summary/SummarySidebar";
import FulfillmentTab from "@widgets/pos/order/fulfillment/FulfillmentTab";
import MenuTab from "@widgets/pos/order/menu/MenuTab";
import CustomerTab from "@widgets/pos/order/customer/CustomerTab";
import {
    PlacedOrderOverview,
    PosOrderEdit,
    PosOrderForm,
    PosOrderPlacement,
    PosOrderUpdate,
    REQUESTED_TIME_ASAP
} from "@/data/entities/order";
import {FormProvider, useForm} from "react-hook-form";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {useIntl} from "react-intl";
import {POS_ORDER_QUERY_KEY} from "@/context/pos/UsePosOrder";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {posOrders} from "@/routing/Router";
import {POS_ORDERS_QUERY_KEY, usePos} from "@/context/pos/PosProvider";
import {useNavigate} from "react-router-dom";
import {api} from "@/data/api";
import {MenuProvider} from "@widgets/pos/order/hooks/MenuProvider";
import {ExistingOrder, ExistingOrderProvider} from "@widgets/pos/order/hooks/UseExistingOrder";
import dayjs from "dayjs";

const EMPTY_ORDER_FORM: PosOrderForm = {
    products: [],
    customerId: null,
    contactDetails: {firstName: "", lastName: "", phoneNumber: null, emailAddress: null},
    deliveryAddress: {type: "TAKEAWAY"},
    businessDetails: null,
    requestedTime: REQUESTED_TIME_ASAP,
    note: null,
    paymentStatus: "PAY_LATER",
    customerType: "GUEST",
    customer: null,
    discount: null,
};

const FULFILLMENT_TAB_INDEX = 0;
const MENU_TAB_INDEX = 1;
/** Index of the customer details tab, where all form fields that can be invalid are. */
const CUSTOMER_TAB_INDEX = 2;

const toPlacement = (form: PosOrderForm): PosOrderPlacement => ({
    products: form.products,
    customerId: form.customerId,
    guest: form.customerId === null ? form.contactDetails : null,
    deliveryAddress: form.deliveryAddress,
    requestedTime: form.requestedTime,
    businessDetails: form.businessDetails,
    note: form.note,
    paidInPerson: form.paymentStatus === "PAID",
    discount: form.discount,
});

const toUpdate = (form: PosOrderForm): PosOrderUpdate => ({
    products: form.products,
    contactDetails: form.contactDetails,
    deliveryAddress: form.deliveryAddress,
    requestedTime: form.requestedTime,
    businessDetails: form.businessDetails,
    note: form.note,
    paidInPerson: form.paymentStatus === "PAID",
    discount: form.discount,
});

/**
 * Form values of an existing order. The customer account can not be changed.
 */
const toForm = ({order: {paidInPerson, ...order}, customer}: PosOrderEdit): PosOrderForm => ({
    ...order,
    businessDetails: order.businessDetails ?? null,
    note: order.note ?? null,
    discount: order.discount ?? null,
    paymentStatus: paidInPerson ? "PAID" : "PAY_LATER",
    customerType: customer === null ? "GUEST" : "EXISTING",
    customerId: customer?.id ?? null,
    customer,
});

export type OrderEditorTabProps = {
    changeTabRef: React.MutableRefObject<ChangeTabFn | undefined>,
    isPending: boolean,
};

type Props = {
    publicCartId?: string, // undefined = new order
    initialOrder?: PosOrderEdit,
};

const OrderEditor = ({publicCartId, initialOrder}: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const {dashboardLocation} = useRestaurant();
    const queryClient = useQueryClient();
    const {notify} = useNotifications();
    const changeTabRef = useRef<ChangeTabFn>();
    const {setSelectedDay} = usePos();
    const existingOrder: ExistingOrder | null = useMemo(
        () => publicCartId !== undefined && initialOrder !== undefined ? {...initialOrder, publicCartId} : null,
        [publicCartId, initialOrder],
    );
    const form = useForm<PosOrderForm>({
        defaultValues: initialOrder === undefined ? EMPTY_ORDER_FORM : toForm(initialOrder),
    });
    // const form = useForm<PosOrderForm>({
    //     defaultValues: initialOrder === undefined
    //         ? EMPTY_ORDER_FORM
    //         : {
    //             ...initialOrder,
    //             // customerId: null, // todo - these props need to be set, but arent given from initial order
    //             // orderWithExistingUser: initialOrder., // todo - these props need to be set, but arent given from initial order
    //         },
    // });
    const {mutate, isPending} = useMutation({
        mutationFn: (values: PosOrderForm) => publicCartId === undefined
            ? api.orders.placeOrder(dashboardLocation.id, toPlacement(values))
            : api.orders.updateOrder(dashboardLocation.id, publicCartId, toUpdate(values)),
        onSuccess: (order: PlacedOrderOverview) => {
            notifySuccess(intl.formatMessage({id: "saved"}), notify);
            queryClient.setQueryData([POS_ORDER_QUERY_KEY, dashboardLocation.id, order.cartPublicId], order);
            void queryClient.invalidateQueries({queryKey: [POS_ORDERS_QUERY_KEY]});
            setSelectedDay(order.fulfillmentDate === dayjs().format("YYYY-MM-DD") ? null : order.fulfillmentDate); // Show the order in the list of its day (an order in advance is not in today's list)
            navigate(posOrders(order.cartPublicId).absolutePath());
        },
        onError: () => {
            notifyDanger(intl.formatMessage({id: "orderCouldNotBeSaved"}), notify);
        },
    });
    const goToCustomerTab = () => changeTabRef.current?.(CUSTOMER_TAB_INDEX);
    const validateAndSave: () => void = form.handleSubmit(
        (values: PosOrderForm) => mutate(values),
        goToCustomerTab,
    );
    // Fields are only validated while they are mounted, so first open the tab
    // with the customer details and validate once it has rendered.
    const onSubmit = () => {
        goToCustomerTab();
        setTimeout(validateAndSave, 0);
    };
    const tabProps: OrderEditorTabProps = {isPending, changeTabRef};

    console.log("initialOrder", initialOrder)

    // TODO - When on order page, and clicking "New order"-button, we need to reset the entire state.

    return (
        <FormProvider {...form}>
            <ExistingOrderProvider value={existingOrder}>
                <MenuProvider>
                    <section className={"h-full grid grid-cols-[1fr_440px] grid-rows-1"}>
                        <section className={"min-h-0 min-w-0"}>
                            <TabControl
                                containerClassname={"h-full min-h-0 flex flex-col"}
                                tabsListContainerClassname={"shrink-0"}
                                tabPanelsContainerClassname={"flex-1 min-h-0 overflow-y-auto"}
                                initialSelectedIndex={existingOrder === null ? FULFILLMENT_TAB_INDEX : MENU_TAB_INDEX}
                                changeTabFnRef={changeTabRef}
                                tabsListStyle={{
                                    style: {
                                        button: {
                                            color: 'secondary',
                                            overridePadding: false,
                                            size: 'sm',
                                            variant: 'text'
                                        },
                                        containerClassName: twJoin(
                                            'flex flex-row justify-center gap-4 overflow-x-auto w-full px-6 py-3 bg-gray-50 dark:bg-slate-800 border-b',
                                            defaultBorderColor,
                                        )
                                    },
                                    type: 'button-row'
                                }}
                            >
                                {FulfillmentTab(tabProps)}
                                {MenuTab(tabProps)}
                                {CustomerTab(tabProps)}
                            </TabControl>
                        </section>

                        <SummarySidebar
                            {...tabProps}
                            onSubmit={onSubmit}
                        />
                    </section>
                </MenuProvider>
            </ExistingOrderProvider>
        </FormProvider>
    );
};

export default OrderEditor;